import { useEffect, useState } from "react";
import swal from "../../utils/swal";
import {
  useDeleteTeacherDutyDay,
  useGetTeacherRules,
  usePendingTeacherLeaves,
  useReviewTeacherLeave,
  useSaveTeacherDutyDay,
  useTeacherDutyDays,
  useUpdateTeacherRules,
} from "../../query/settingsQuery";
import { DEFAULT_SCHOOL_TIME_ZONE, schoolZoneOptions } from "../../utils/schoolTime";

const EMPTY = {
  timeZone: DEFAULT_SCHOOL_TIME_ZONE,
  checkInClock: "07:00",
  graceMinutes: 15,
  checkOutClock: "14:00",
  leaveQuota: { SICK: 8, CASUAL: 8, ANNUAL: 10 },
};

function teacherName(teacher) {
  if (!teacher || typeof teacher === "string") return "Teacher";
  return `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim() || "Teacher";
}

function dayLabel(value, timeZone = DEFAULT_SCHOOL_TIME_ZONE) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-PK", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone,
  });
}

export default function TeacherAttendancePanel() {
  const { data: rules, isLoading } = useGetTeacherRules();
  const { data: leaves = [], isLoading: leavesLoading } = usePendingTeacherLeaves();
  const { data: dutyDays = [] } = useTeacherDutyDays();
  const { mutate: saveRules, isPending } = useUpdateTeacherRules();
  const { mutate: review, isPending: reviewing } = useReviewTeacherLeave();
  const { mutate: saveDuty, isPending: savingDuty } = useSaveTeacherDutyDay();
  const { mutate: removeDuty, isPending: removingDuty } = useDeleteTeacherDutyDay();
  const [form, setForm] = useState(EMPTY);
  const [duty, setDuty] = useState({
    name: "",
    startKey: "",
    endKey: "",
    checkInClock: "12:00",
    checkOutClock: "16:00",
  });

  useEffect(() => {
    if (!rules) return;
    setForm({
      timeZone: rules.timeZone || DEFAULT_SCHOOL_TIME_ZONE,
      checkInClock: rules.checkInClock || "07:00",
      graceMinutes: rules.graceMinutes ?? 15,
      checkOutClock: rules.checkOutClock || "14:00",
      leaveQuota: {
        SICK: rules.leaveQuota?.SICK ?? 8,
        CASUAL: rules.leaveQuota?.CASUAL ?? 8,
        ANNUAL: rules.leaveQuota?.ANNUAL ?? 10,
      },
    });
  }, [rules]);

  const setQuota = (key, value) => {
    setForm((current) => ({
      ...current,
      leaveQuota: { ...current.leaveQuota, [key]: value },
    }));
  };

  const save = () => {
    saveRules(form, {
      onSuccess: () => swal("Saved", "Teacher attendance rules updated", "success"),
      onError: (error) => swal("Error", error?.message || "Could not save", "error"),
    });
  };

  const addDuty = () => {
    saveDuty(duty, {
        onSuccess: (response) => {
        const sent = Boolean(response?.data?.data?.noticeSent);
        swal(
          "Saved",
          sent
            ? "That day is open for every teacher, and they were sent a notice with the time."
            : "That day is open for every teacher. The notice could not be sent.",
          sent ? "success" : "warning"
        );
        setDuty((current) => ({ ...current, name: "", startKey: "", endKey: "" }));
      },
      onError: (error) => swal("Error", error?.message || "Could not save special day", "error"),
    });
  };

  const decide = (id, decision) => {
    review(
      { id, decision },
      {
        onSuccess: () =>
          swal("Updated", decision === "APPROVED" ? "Leave approved" : "Leave rejected. That day is now absent", "success"),
        onError: (error) => swal("Error", error?.message || "Could not update leave", "error"),
      }
    );
  };

  if (isLoading && !rules) {
    return (
      <div className="settings-panel-body">
        <p className="settings-muted">Loading teacher attendance rules…</p>
      </div>
    );
  }

  return (
    <div className="settings-panel-body">
      <div className="settings-panel-intro">
        <div>
          <h2>Teacher attendance</h2>
          <p>
            Clocks are school time ({form.timeZone}). Check-in opens one hour before {form.checkInClock} and
            stays on time through the grace window. After that the day is absent until they check in, and that check-in is marked late. Check-out cannot happen
            before {form.checkOutClock}. Weekends are off unless you add an open weekend below. That day uses its own clock, students stay home, and every teacher gets a notice.
          </p>
        </div>
      </div>

      <div className="settings-rule-grid">
        <label>
          School timezone
          <select
            value={form.timeZone}
            onChange={(event) => setForm((current) => ({ ...current, timeZone: event.target.value }))}
          >
            {schoolZoneOptions().map((zone) => (
              <option key={zone} value={zone}>
                {zone}
              </option>
            ))}
          </select>
        </label>
        <label>
          Check-in
          <input
            type="time"
            value={form.checkInClock}
            onChange={(event) => setForm((current) => ({ ...current, checkInClock: event.target.value }))}
          />
        </label>
        <label>
          Grace minutes
          <input
            type="number"
            min="0"
            max="180"
            value={form.graceMinutes}
            onChange={(event) => setForm((current) => ({ ...current, graceMinutes: event.target.value }))}
          />
        </label>
        <label>
          Check-out
          <input
            type="time"
            value={form.checkOutClock}
            onChange={(event) => setForm((current) => ({ ...current, checkOutClock: event.target.value }))}
          />
        </label>
        <label>
          Sick days
          <input type="number" min="0" value={form.leaveQuota.SICK} onChange={(event) => setQuota("SICK", event.target.value)} />
        </label>
        <label>
          Casual days
          <input type="number" min="0" value={form.leaveQuota.CASUAL} onChange={(event) => setQuota("CASUAL", event.target.value)} />
        </label>
        <label>
          Annual days
          <input type="number" min="0" value={form.leaveQuota.ANNUAL} onChange={(event) => setQuota("ANNUAL", event.target.value)} />
        </label>
      </div>
      <button type="button" className="settings-save" onClick={save} disabled={isPending}>
        {isPending ? "Saving…" : "Save rules"}
      </button>

      <h3 className="settings-subhead">Open weekends</h3>
      <p className="settings-muted">
        Add a Saturday or Sunday here when all teachers must come in. Set the check-in and check-out time.
        Saving sends every teacher a notice with the day and those times. Students stay home.
      </p>
      <div className="settings-rule-grid">
        <label>
          Name
          <input
            value={duty.name}
            placeholder="Open Saturday"
            onChange={(event) => setDuty((current) => ({ ...current, name: event.target.value }))}
          />
        </label>
        <label>
          Start
          <input
            type="date"
            value={duty.startKey}
            onChange={(event) => setDuty((current) => ({ ...current, startKey: event.target.value }))}
          />
        </label>
        <label>
          End
          <input
            type="date"
            value={duty.endKey}
            onChange={(event) => setDuty((current) => ({ ...current, endKey: event.target.value }))}
          />
        </label>
        <label>
          Check-in
          <input
            type="time"
            value={duty.checkInClock}
            onChange={(event) => setDuty((current) => ({ ...current, checkInClock: event.target.value }))}
          />
        </label>
        <label>
          Check-out
          <input
            type="time"
            value={duty.checkOutClock}
            onChange={(event) => setDuty((current) => ({ ...current, checkOutClock: event.target.value }))}
          />
        </label>
      </div>
      <button type="button" className="settings-save" onClick={addDuty} disabled={savingDuty || !duty.startKey}>
        {savingDuty ? "Saving…" : "Add open weekend"}
      </button>
      <div className="settings-leave-list">
        {dutyDays.map((item) => (
          <div className="settings-leave-row" key={item._id}>
            <div>
              <strong>{item.name || "Open weekend"}</strong>
              <span>
                {item.startKey}
                {item.endKey && item.endKey !== item.startKey ? ` to ${item.endKey}` : ""} · in {item.checkInLabel} · out {item.checkOutLabel}
              </span>
            </div>
            <div className="settings-leave-actions">
              <button type="button" className="is-quiet" disabled={removingDuty} onClick={() => removeDuty(item._id)}>
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      <h3 className="settings-subhead">Leave waiting for approval</h3>
      {leavesLoading ? <p className="settings-muted">Loading requests…</p> : null}
      {!leavesLoading && !leaves.length ? <p className="settings-muted">No pending leave.</p> : null}
      <div className="settings-leave-list">
        {leaves.map((item) => (
          <div className="settings-leave-row" key={item._id}>
            <div>
              <strong>{teacherName(item.teacher)}</strong>
              <span>
                {dayLabel(item.checkIn, form.timeZone)} · {item.leaveType || "Leave"}
                {item.leaveReason ? ` · ${item.leaveReason}` : ""}
              </span>
            </div>
            <div className="settings-leave-actions">
              <button type="button" disabled={reviewing} onClick={() => decide(item._id, "APPROVED")}>
                Approve
              </button>
              <button type="button" className="is-quiet" disabled={reviewing} onClick={() => decide(item._id, "REJECTED")}>
                Reject
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
